/**
 * seedTiposTarea.js — Seed canónico de tipos de tarea + migración legacy
 *
 * LA FUENTE DE VERDAD del catálogo (datos) vive en ./catalogoTiposTarea.js —
 * la comparten este seed (consola del browser) y el seed de terminal
 * (scripts/seed-tipos-tarea-v1.cjs, firebase-admin + ADC). Editar el catálogo
 * SIEMPRE ahí, nunca acá.
 *
 * CERO LEGACY (11-jul-2026): los tipos viejos NO se marcan legacy — se
 * migran en duro y se borran con scripts/migrar-cero-legacy.cjs (terminal).
 * Este seed solo siembra canónicos y desactiva los tipos fuera de catálogo.
 *
 * Uso desde consola del browser:
 *   await window.__seedTiposTareaCanonicos()
 *   await window.__seedTiposTareaCanonicos({ dryRun: true })  // solo loguea, no escribe
 *
 * Uso desde terminal (equivalente, sin browser):
 *   node scripts/seed-tipos-tarea-v1.cjs --project=milbrands-cowork-staging --dry-run
 */

import {
  collection, doc, getDocs, setDoc, updateDoc, query, where,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { TIPOS_CANONICOS, MIGRAR_Y_BORRAR, DESACTIVAR } from './catalogoTiposTarea'

// Re-export para consumidores existentes del catálogo.
export { TIPOS_CANONICOS, MIGRAR_Y_BORRAR, DESACTIVAR }

// ════════════════════════════════════════════════════════════════
// Función de seed
// ════════════════════════════════════════════════════════════════

export async function seedTiposTarea({ dryRun = false } = {}) {
  console.groupCollapsed(`[seedTiposTarea] dryRun=${dryRun}`)

  // 1. Listar lo que ya existe (idempotencia)
  const snap = await getDocs(collection(db, COLLECTIONS.TIPOS_TAREA))
  const existentesPorCodigo = new Map()
  snap.forEach(d => {
    const tipo = docConId(d)
    if (tipo.codigo) existentesPorCodigo.set(tipo.codigo, tipo)
  })

  const creados = []
  const actualizados = []
  const omitidos = []

  // 2. Sembrar canónicos
  for (const tipo of TIPOS_CANONICOS) {
    const existente = existentesPorCodigo.get(tipo.codigo)
    const payload = {
      codigo: tipo.codigo,
      nombre: tipo.nombre,
      descripcion: tipo.descripcion || 'Tipo canónico verbo+elemento. Ver docs/modelo-tareas.md.',
      cierre: tipo.cierre,
      categoria: tipo.categoria,
      slaDefaultMinutos: tipo.slaDefaultMinutos,
      ventanaAlertaMinutos: 15,
      prioridadDefault: tipo.prioridadDefault,
      permitirAgrupacion: true,
      legacy: false,
      redirigeA: null,
      activo: true,
      orden: tipo.orden,
      // Metadatos del agente global (impacto × esfuerzo) + bitácora
      esfuerzo: tipo.esfuerzo ?? null,
      etiqueta: tipo.etiqueta ?? null,
      emiteEventoBitacora: !!tipo.emiteEventoBitacora,
      cuotaProduccion: tipo.cuotaProduccion ?? null,
      // Acumula hallazgos de varias fuentes en la MISMA pub (merge por publicación).
      // Los tipos con este flag no los frena la regla de "lote en curso".
      fusionaPorPublicacion: !!tipo.fusionaPorPublicacion,
      sugiereCaso: !!tipo.sugiereCaso,
      tipoCasoSugerido: tipo.tipoCasoSugerido ?? null,
      actualizadoEn: serverTimestamp(),
    }
    if (existente) {
      // Update merge — preserva flags que el admin haya editado.
      if (!dryRun) {
        await setDoc(doc(db, COLLECTIONS.TIPOS_TAREA, existente.id), payload, { merge: true })
      }
      actualizados.push(tipo.codigo)
      console.log(`[seedTiposTarea] ♻️  ${tipo.codigo} actualizado`)
    } else {
      payload.creadoEn = serverTimestamp()
      // Usamos el código como ID para que sea idempotente y referenciable.
      if (!dryRun) {
        await setDoc(doc(db, COLLECTIONS.TIPOS_TAREA, tipo.codigo), payload)
      }
      creados.push(tipo.codigo)
      console.log(`[seedTiposTarea] ➕ ${tipo.codigo} creado`)
    }
  }

  // 3. CERO LEGACY: la migración dura (tareas + mapeos + borrado de tipos
  //    viejos) corre por terminal: scripts/migrar-cero-legacy.cjs.
  //    migrados/omitidos quedan vacíos acá para conservar el shape del retorno.
  const migrados = []

  // 4. Desactivar tipos fuera del catálogo (limpieza 11-jul — soft delete,
  //    reactivable desde /slas; las tareas existentes no se tocan)
  const desactivados = []
  for (const codigo of DESACTIVAR) {
    const existente = existentesPorCodigo.get(codigo)
    if (!existente || existente.activo === false) continue
    if (!dryRun) {
      await updateDoc(doc(db, COLLECTIONS.TIPOS_TAREA, existente.id), {
        activo: false,
        actualizadoEn: serverTimestamp(),
      })
    }
    desactivados.push(codigo)
  }

  console.log(`[seedTiposTarea] creados=${creados.length} actualizados=${actualizados.length} migrados=${migrados.length} omitidos=${omitidos.length}`)
  console.groupEnd()

  return { creados, actualizados, migrados, omitidos, desactivados }
}

// ════════════════════════════════════════════════════════════════
// Mapeo (area__seccion) → tipoTareaId — qué tipo crea cada sección
// ════════════════════════════════════════════════════════════════
//
// Vive en `/configuracion/mapeoSeccionesTareas` como un único doc con un
// objeto. Cuando un dashboard crea una tarea, busca el tipoTareaId de su
// `(area__seccion)` para crearla con el código canónico correcto.
//
// ⚠️ OJO (11-jul-2026): este mapa está DESACTUALIZADO respecto de los mapeos
// vivos que se editan por engranaje en cada dashboard. NO correr
// seedMapeoSeccionesTareas sin revisarlo — pisaría mapeos configurados.

export const MAPEO_SECCIONES = {
  // ─── Postventa MVP (5 secciones) ────────────────────────────
  'postventa__modificar_publicacion': 'modificar_publicacion',
  'postventa__pausar_publicacion':    'pausar_publicacion',
  'postventa__cambiar_transportista': 'cambiar_transportista',
  'postventa__cambiar_proveedor':     'cambiar_proveedor',
  'postventa__cambiar_embalaje':      'cambiar_embalaje',

  // ─── Preguntas y Respuestas (re-routing de legacy a canónicos) ─
  // Lo que antes apuntaba a `mejorar_publicacion_pyr` ahora va al canónico.
  'pyr__mejorar_publicacion':    'modificar_publicacion',
  'pyr__crear_clip':             'crear_clip',
  'pyr__atender_horario':        'cubrir_horario',

  // ─── Opiniones (idem) ───────────────────────────────────────
  'opiniones__mejorar_publicacion':       'modificar_publicacion',
  'opiniones__atender_quejas_recurrentes': 'modificar_publicacion',

  // ─── Publicidad ─────────────────────────────────────────────
  'publicidad__pausar_campanas':      'pausar_ad',
  'publicidad__activar_campanas':     'activar_ad',
  'publicidad__optimizar_presupuesto': 'modificar_descuento',  // ajustar presupuesto
  'publicidad__revisar_acos_alto':    'pausar_ad',
  'publicidad__escalar_campanas_rentables': 'activar_ad',

  // ─── Rentabilidad (Contribución Neta por Orden — mayo 2026) ──
  // El agente analiza órdenes con contribución < 0 y decide:
  //   - pausar el más caro (ad o promo) según el peso en esa orden
  //   - si simulando la pausa la orden sigue negativa, también pausar el otro
  //   - si la pub no tiene ni ad ni promo (o ya se intentaron en últimos 30d
  //     según bitácora), sugerir subir precio al netoPctProm de la cuenta
  'rentabilidad__pausar_ad':     'pausar_ad',
  'rentabilidad__pausar_promo':  'pausar_promo',
  'rentabilidad__subir_precio':  'subir_precio',
  'rentabilidad__mejorar_margen': 'mejorar_margen',
  // 4ta sección agregada en mayo 2026: pubs con margen<netoPctProm o
  // palanca apagada sin orden negativa concreta. Tipo propio porque no
  // tiene acción mecánica — el operador investiga (competencia, listing,
  // pricing) y decide.
  'rentabilidad__revisar_competitividad': 'revisar_competitividad',

  // ─── Rentabilidad inferida (dashboard violeta — jul 2026) ────
  // Universo DISJUNTO de Contribución Neta: pubs SIN venta en la ventana,
  // margen INFERIDO con la fórmula completa (rentabilidadEstimada). Ambas
  // secciones emiten subir_precio (al precio sugerido que deja la renta
  // FINAL en la mínima); la sección define la urgencia (pérdida=alta,
  // bajo la mínima=media) vía SECCION_DEFAULTS.
  'rentabilidad-inferida__perdida_inferida':     'subir_precio',
  'rentabilidad-inferida__bajo_minima_inferida': 'subir_precio',

  // ─── Productos — carga de datos del catálogo (16-jul-2026) ────
  // Tarea manual desde la página Productos: productos con vínculo ML sin
  // costo y/o tiempo de reposición → una tarea única con el CSV pre-llenado
  // adjunto (se completa y se importa por "Importar costos CSV"). Reusa el
  // tipo canónico de datos faltantes (mismo que velocidad F8.6).
  'productos__productos_sin_datos': 'completar_datos_transaccionales',
}

export async function seedMapeoSeccionesTareas({ dryRun = false } = {}) {
  console.groupCollapsed(`[seedMapeoSeccionesTareas] dryRun=${dryRun}`)
  const ref = doc(db, COLLECTIONS.CONFIGURACION || 'configuracion', 'mapeoSeccionesTareas')
  const payload = {
    mapa: MAPEO_SECCIONES,
    actualizadoEn: serverTimestamp(),
  }
  if (!dryRun) {
    await setDoc(ref, payload, { merge: true })
  }
  console.log(`[seedMapeoSeccionesTareas] sembrado ${Object.keys(MAPEO_SECCIONES).length} entradas`)
  console.groupEnd()
  return { entradas: Object.keys(MAPEO_SECCIONES).length }
}

// ════════════════════════════════════════════════════════════════
// Exposición en window para uso desde consola admin
// ════════════════════════════════════════════════════════════════

if (typeof window !== 'undefined') {
  // OJO: `__seedTiposTarea` (sin sufijo) ya existe en `scripts/seed-tipos-tarea.js`
  // y siembra tipos viejos. Esta función nueva siembra los canónicos verbo+elemento
  // (catálogo v1) + migra los legacy. Convive con el seed viejo.
  window.__seedTiposTareaCanonicos = seedTiposTarea
  window.__seedMapeoSeccionesTareas = seedMapeoSeccionesTareas
}
